export type QueryPrimitive = string | number | boolean | null | undefined
export type QueryValue = QueryPrimitive | readonly QueryPrimitive[]
export type QueryParams = Record<string, QueryValue> | URLSearchParams

export type ApiRequestOptions = {
    query?: QueryParams
    signal?: AbortSignal
    headers?: HeadersInit
    body?: unknown
    timeoutMs?: number
}

export type ApiClientOptions = {
    baseUrl: string
    timeoutMs?: number
    fetch?: typeof fetch
    getHeaders?: () => HeadersInit | Promise<HeadersInit>
    getCredentials?: () => RequestCredentials | Promise<RequestCredentials>
    onUnauthorized?: () => void
}

export class ApiError extends Error {
    readonly status: number
    readonly body: unknown
    readonly url: string

    constructor(message: string, status: number, body: unknown, url: string) {
        super(message)
        this.name = "ApiError"
        this.status = status
        this.body = body
        this.url = url
    }
}

export class ApiUnauthorizedError extends ApiError {
    constructor(message: string, body: unknown, url: string) {
        super(message, 401, body, url)
        this.name = "ApiUnauthorizedError"
    }
}

export class ApiNetworkError extends Error {
    readonly url: string

    constructor(message: string, url: string, options?: ErrorOptions) {
        super(message, options)
        this.name = "ApiNetworkError"
        this.url = url
    }
}

export class ApiRequestAbortedError extends ApiNetworkError {
    constructor(message: string, url: string, options?: ErrorOptions) {
        super(message, url, options)
        this.name = "ApiRequestAbortedError"
    }
}

export class ApiTimeoutError extends ApiNetworkError {
    readonly timeoutMs: number

    constructor(message: string, url: string, timeoutMs: number, options?: ErrorOptions) {
        super(message, url, options)
        this.name = "ApiTimeoutError"
        this.timeoutMs = timeoutMs
    }
}

function normalizeBaseUrl(value: string) {
    return value.replace(/\/+$/, "")
}

function appendQuery(url: URL, query?: QueryParams) {
    if (!query) {
        return
    }

    if (query instanceof URLSearchParams) {
        for (const [key, value] of query) {
            url.searchParams.append(key, value)
        }
        return
    }

    for (const [key, rawValue] of Object.entries(query)) {
        const values = Array.isArray(rawValue) ? rawValue : [rawValue]

        for (const value of values) {
            if (value === undefined || value === null || value === "") {
                continue
            }

            url.searchParams.append(key, String(value))
        }
    }
}

async function parseResponseBody(response: Response) {
    if (response.status === 204) {
        return null
    }

    const contentType = response.headers.get("content-type") ?? ""

    if (contentType.includes("application/json")) {
        return await response.json()
    }

    const text = await response.text()
    return text.length > 0 ? text : null
}

function getErrorMessage(body: unknown, fallback: string) {
    if (typeof body === "object" && body !== null && "message" in body && typeof body.message === "string") {
        return body.message
    }

    return fallback
}

function createTimeoutSignal(parentSignal: AbortSignal | undefined, timeoutMs: number) {
    const controller = new AbortController()
    let abortedBy: "caller" | "timeout" | null = null
    const abortFromParent = () => {
        if (abortedBy !== null) return
        abortedBy = "caller"
        controller.abort(parentSignal?.reason)
    }
    const timeoutId = setTimeout(() => {
        if (abortedBy !== null) return
        abortedBy = "timeout"
        controller.abort(new Error(`Request timed out after ${timeoutMs}ms`))
    }, timeoutMs)

    if (parentSignal) {
        if (parentSignal.aborted) {
            abortFromParent()
        } else {
            parentSignal.addEventListener("abort", abortFromParent, { once: true })
        }
    }

    return {
        signal: controller.signal,
        getAbortedBy: () => abortedBy,
        cleanup: () => {
            clearTimeout(timeoutId)
            parentSignal?.removeEventListener("abort", abortFromParent)
        },
    }
}

export function createApiClient(options: ApiClientOptions) {
    const baseUrl = normalizeBaseUrl(options.baseUrl)
    const requestFetch = options.fetch ?? globalThis.fetch
    const defaultTimeoutMs = options.timeoutMs ?? 10_000

    async function request<T>(method: string, path: string, requestOptions: ApiRequestOptions = {}): Promise<T> {
        const normalizedPath = path.startsWith("/") ? path : `/${path}`
        const url = new URL(`${baseUrl}${normalizedPath}`)
        appendQuery(url, requestOptions.query)

        const timeoutMs = requestOptions.timeoutMs ?? defaultTimeoutMs
        const { signal, getAbortedBy, cleanup } = createTimeoutSignal(requestOptions.signal, timeoutMs)
        const headers = new Headers(await options.getHeaders?.())

        for (const [key, value] of new Headers(requestOptions.headers)) {
            headers.set(key, value)
        }

        headers.set("Accept", "application/json")

        if (requestOptions.body !== undefined) {
            headers.set("Content-Type", "application/json")
        }

        try {
            const response = await requestFetch(url, {
                method,
                headers,
                body: requestOptions.body === undefined ? undefined : JSON.stringify(requestOptions.body),
                credentials: (await options.getCredentials?.()) ?? "include",
                signal,
            })
            const body = await parseResponseBody(response)

            if (!response.ok) {
                const message = getErrorMessage(body, `Request failed with status ${response.status}`)

                if (response.status === 401) {
                    options.onUnauthorized?.()
                    throw new ApiUnauthorizedError(message, body, url.toString())
                }

                throw new ApiError(message, response.status, body, url.toString())
            }

            return body as T
        } catch (error) {
            if (error instanceof ApiError) {
                throw error
            }

            if (getAbortedBy() === "caller") {
                throw new ApiRequestAbortedError("Request was aborted", url.toString(), { cause: error })
            }

            if (getAbortedBy() === "timeout") {
                throw new ApiTimeoutError(`Request timed out after ${timeoutMs}ms`, url.toString(), timeoutMs, {
                    cause: error,
                })
            }

            throw new ApiNetworkError("Network request failed", url.toString(), { cause: error })
        } finally {
            cleanup()
        }
    }

    return {
        get: <T>(path: string, requestOptions?: Omit<ApiRequestOptions, "body">) =>
            request<T>("GET", path, requestOptions),
        post: <T>(path: string, body?: unknown, requestOptions?: Omit<ApiRequestOptions, "body">) =>
            request<T>("POST", path, { ...requestOptions, body }),
        put: <T>(path: string, body?: unknown, requestOptions?: Omit<ApiRequestOptions, "body">) =>
            request<T>("PUT", path, { ...requestOptions, body }),
        patch: <T>(path: string, body?: unknown, requestOptions?: Omit<ApiRequestOptions, "body">) =>
            request<T>("PATCH", path, { ...requestOptions, body }),
        delete: <T>(path: string, requestOptions?: Omit<ApiRequestOptions, "body">) =>
            request<T>("DELETE", path, requestOptions),
    }
}

export type ApiClient = ReturnType<typeof createApiClient>
